import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { QsHelper, odometerHelper } from '../../../../../helpers';

import moment from 'moment';
import parseISO from 'date-fns/parseISO';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import {
  HeartIcon,
  FuelIcon,
  RpmIcon,
  CoolantIcon,
  OdometerIcon,
  BatteryIcon,
  OilIcon,
} from '../../../../../common/icons';

import PanelPosition from './PanelPosition';
import FocusGauge from './FocusGauge';
import FuelDataModal from './FuelDataModal';
import RpmDataModal from './RpmDataModal';

import './QuickFocus.css';
import QuickGroupMaintenances from '../QuickHealth/QuickGroupMaintenances';
import LockUnlockPanel from '../LockUnlockPanel/LockUnlockPanel';
import { authStore } from '../../../../../stores';

const serviceLevelsWithLimitedFeatures = ['light-a'];

@withRouter
@inject('translationsStore', 'commonStore', 'authStore')
@observer
export default class QuickFocus extends Component {
  constructor(props) {
    super(props);
    this.state = {
      modalFuelVisible: false,
      rpmModalVisible: false,
    };
  }

  componentDidMount = () => {
    this.setState({
      modalFuelVisible: this.modalFuel(),
      rpmModalVisible: this.modalRpm(),
    });
  };

  componentDidUpdate = (prevProps) => {
    if (
      this.props.modal !== prevProps.modal ||
      (this.props.car || {}).id !== (prevProps.car || {}).id
    ) {
      this.setState({
        modalFuelVisible: this.modalFuel(),
        rpmModalVisible: this.modalRpm(),
      });
    }
  };

  modalFuel = () => this.props.modal === 'fuel' || false;

  modalRpm = () => this.props.modal === 'rpm' || false;

  helperStatusValue = (car) => {
    const { translationsStore } = this.props;
    let toRender = [];
    switch (car.status) {
      case 'connected':
        toRender = (
          <div style={{ color: 'var(--primary-color)' }}>
            <HeartIcon style={{ width: '32px', height: '32px' }} />
            <div>
              {translationsStore.translate(
                'quickFocus.position.status.connected'
              )}
            </div>
          </div>
        );
        break;
      default:
        toRender = (
          <div>
            <HeartIcon style={{ width: '32px', height: '32px' }} />
            <div>
              {translationsStore.translate(
                'quickFocus.position.status.unknown'
              )}
            </div>
          </div>
        );
        break;
    }
    return toRender;
  };

  helperGaugeIconValue = (car, key) => {
    const { commonStore } = this.props;
    let toRenderValue = '-';
    let toRenderIcon = <OdometerIcon width='20px' height='20px' />;
    let style = { color: 'var(--dark-grey)' };
    switch (key) {
      case 'fuelLevel':
        toRenderIcon = <FuelIcon width='18px' height='20px' />;
        if (moment(car.fuelLevel.updatedAt).isValid()) {
          const fuelLevelValue = Number(car.fuelLevel.value).toFixed(0);
          style = { ...style, color: 'var(--green)' };
          if (fuelLevelValue < 20) {
            style = { ...style, color: 'var(--orange-dtc-color)' };
          }
          if (fuelLevelValue < 10) {
            style = { ...style, color: 'var(--red-dtc-color)' };
          }
          toRenderValue = `${fuelLevelValue.toLocaleString(
            commonStore.momentLocale
          )}%`;
        }
        break;
      case 'rpm':
        toRenderIcon = <RpmIcon width='18px' height='20px' />;
        if (car.rpm && moment(car.rpm.updatedAt).isValid()) {
          const rpmValue = Number(car.rpm.value);
          style = { ...style, color: 'var(--dark-grey)' };
          toRenderValue = `${rpmValue.toLocaleString(
            commonStore.momentLocale
          )}`;
        }
        break;
      case 'coolerTemp':
        toRenderIcon = <CoolantIcon width='24px' height='20px' />;
        if (moment(car.coolerTemp.updatedAt).isValid()) {
          const coolerTempValue = Number(car.coolerTemp.value).toFixed(0);
          style = { ...style, color: 'var(--green)' };
          if (coolerTempValue < 10) {
            style = { ...style, color: 'var(--orange-dtc-color)' };
          }
          if (coolerTempValue < 0) {
            style = { ...style, color: 'var(--red-dtc-color)' };
          }
          toRenderValue = `${coolerTempValue}°`;
        }
        break;
      case 'odometer':
        toRenderIcon = <OdometerIcon width='20px' height='20px' />;
        if (moment(car.odometer.updatedAt).isValid()) {
          const odometerValue = Number(car.odometer.value).toFixed(0);
          toRenderValue = `${odometerValue.toLocaleString(
            commonStore.momentLocale
          )}`;
        }
        break;
      case 'gpsOdometer':
        toRenderIcon = <OdometerIcon width='20px' height='20px' />;
        if (moment(car.gpsOdometer.updatedAt).isValid()) {
          const { initialOdometer, gpsOdometer, gpsOdometerOnDisconnection } =
            car;
          const gpsOdometerValue = Number(
            odometerHelper.computeOdometer({
              initialOdometer,
              gpsOdometer,
              gpsOdometerOnDisconnection,
            })
          ).toFixed(0);
          toRenderValue = `${gpsOdometerValue.toLocaleString(
            commonStore.momentLocale
          )}`;
        } else if (car.initialOdometer > 0) {
          toRenderValue = `${car.initialOdometer.toLocaleString(
            commonStore.momentLocale
          )}`;
        }
        break;
      case 'battery':
        toRenderIcon = <BatteryIcon width='24px' height='19px' />;
        if (moment(car.battery.updatedAt).isValid()) {
          const batteryValue = Number(car.battery.value / 1000).toFixed(1);
          style = { ...style, color: 'var(--green)' };
          if (batteryValue < 12) {
            style = { ...style, color: 'var(--battery-warning-color)' };
          }
          if (batteryValue < 10) {
            style = { ...style, color: 'var(--red-dtc-color)' };
          }
          toRenderValue = `${batteryValue.toLocaleString(
            commonStore.momentLocale
          )} V`;
        }
        break;
      case 'oilLevel':
        toRenderIcon = <OilIcon width='34px' height='19px' />;
        if (moment(car.oilLevel.updatedAt).isValid()) {
          const oilLevelValue = Number(car.oilLevel.value).toFixed(0);
          toRenderValue = `${oilLevelValue.toLocaleString(
            commonStore.momentLocale
          )} mm`;
        }
        break;
      // no default
    }
    return (
      <Fragment>
        <div key='icon' style={style}>
          {toRenderIcon}
        </div>
        <div key='value'>{toRenderValue}</div>
      </Fragment>
    );
  };

  helperDateDiff = (date) => {
    const { commonStore } = this.props;
    // console.log('sss', date, moment(date).isValid());
    return moment(date).isValid()
      ? formatDistanceToNow(parseISO(date), {
          locale: commonStore.dateFnsLocale,
        })
      : null;
  };

  handleFuelGaugeClick = () => {
    this.setState({ modalFuelVisible: true });
  };

  onModalFuelOk = () => {
    this.setState({ modalFuelVisible: false }, () => {
      this.unsetModalInLocation();
    });
  };

  onModalFuelCancel = () => {
    this.setState({ modalFuelVisible: false }, () => {
      this.unsetModalInLocation();
    });
  };

  unsetModalInLocation = () => {
    const { history, car } = this.props;
    const withoutModal = new QsHelper(this.props.location.search)
      .set('modal', '')
      .getQueryString();
    history.push(`/dashboard/${car.id}${withoutModal}`);
  };

  getCarUser = (car) => {
    const { commonStore } = this.props;
    if (commonStore.isFleetCustomer) {
      if (car.driver) {
        car.driver.fullName = `${car.driver.name} ${car.driver.surname}`;
        return car.driver;
      }
    }

    return car.client;
  };

  render() {
    const { translationsStore, car } = this.props;

    let masterdataCollection = [];

    if (car) {
      const carUser = this.getCarUser(car);

      masterdataCollection = [
        [
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelClient'
            ),
            value: carUser.fullName,
            className: 'FocusMasterdataValueJumbo',
            style: {
              textTransform: 'capitalize',
            },
            extra: [
              car.group && car.group.phoneNumber
                ? car.group.phoneNumber
                : carUser.mobile,
              carUser.email,
            ],
          },
        ],
        [
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelMakeModel'
            ),
            value: car.makeModel,
            className: 'FocusMasterdataValueJumbo',
            style: {
              textTransform: 'capitalize',
            },
            extra: [
              <span style={{ textTransform: 'capitalize' }}>
                {car.type.descrizione}
              </span>,
              <span style={{ textTransform: 'capitalize' }}>
                {translationsStore.translate(
                  `alimentation.${car.alimentation}`
                )}
              </span>,
              car.registrationYear,
            ],
          },
        ],
        [
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelPlate'
            ),
            value: car.plate,
            className: 'FocusMasterdataValueJumbo',
          },
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelGroup'
            ),
            value: car.group !== null ? car.group.name : '-',
          },
        ],
        [
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelImei'
            ),
            value: car.imei,
          },
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelVinNumber'
            ),
            value: car.casenumber,
          },
        ],
        [
          {
            label: translationsStore.translate(
              'quickFocus.masterdata.labelAdditionalImei'
            ),
            value: car.additionalAssets ? car.additionalAssets.join(',') : '',
          },
        ],
      ];
    }

    console.log('Rendering QuickFocus for car');

    return (
      <Fragment>
        {car && (
          <div className='QuickFocus'>
            <div className='FocusMasterdata'>
              {masterdataCollection.map((item, index) => (
                <div key={index}>
                  {item.map((cell, cellIndex) => {
                    const className = cell.className
                      ? `FocusMasterdataValue ${cell.className}`
                      : 'FocusMasterdataValue';
                    return (
                      <div key={cellIndex} className='FocusMasterdataCell'>
                        <div className='FocusMasterdataLabel'>
                          {cell.label}:
                        </div>
                        <div className={className} style={cell.style}>
                          <div>{cell.value}</div>
                        </div>
                        {cell.extra &&
                          cell.extra.map((extra, extraIndex) => (
                            <div
                              key={extraIndex}
                              className='FocusMasterdataExtra'
                            >
                              {extra}
                            </div>
                          ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            {!authStore.isServiceManager && (
              <div className='FocusPosition'>
                <PanelPosition
                  car={this.props.car}
                  carAddress={this.props.carAddress}
                  additionalCarAddresses={this.props.additionalCarAddresses}
                />
              </div>
            )}
            {!serviceLevelsWithLimitedFeatures.includes(car.serviceLevel) ||
            (car && car.connectedCarMaintenanceStatus === 'active') ? (
              <div
                className='FocusGauges'
                style={
                  car && car.connectedCarMaintenanceStatus === 'active'
                    ? {
                        justifyContent: 'space-around',
                      }
                    : {}
                }
              >
                {car && car.connectedCarMaintenanceStatus !== 'active' && (
                  <React.Fragment>
                    <FocusGauge
                      title={translationsStore.translate(
                        'quickFocus.gauges.labelFuelLevel'
                      )}
                      valueWithIcon={this.helperGaugeIconValue(
                        car,
                        'fuelLevel'
                      )}
                      dateDiff={this.helperDateDiff(car.fuelLevel.updatedAt)}
                      onClick={this.handleFuelGaugeClick}
                    />
                    <FuelDataModal
                      car={this.props.car}
                      visible={this.state.modalFuelVisible}
                      onOk={this.onModalFuelOk}
                      onCancel={this.onModalFuelCancel}
                    />
                  </React.Fragment>
                )}
                {car && car.connectedCarMaintenanceStatus !== 'active' && (
                  <FocusGauge
                    title={translationsStore.translate(
                      'quickFocus.gauges.labelRpm'
                    )}
                    valueWithIcon={this.helperGaugeIconValue(car, 'rpm')}
                    dateDiff={
                      car.rpm ? this.helperDateDiff(car.rpm.updatedAt) : '-'
                    }
                    onClick={() => {
                      this.setState({
                        rpmModalVisible: true,
                      });
                    }}
                  />
                )}
                {car && car.connectedCarMaintenanceStatus !== 'active' && (
                  <FocusGauge
                    title={translationsStore.translate(
                      'quickFocus.gauges.labelCoolerTemp'
                    )}
                    valueWithIcon={this.helperGaugeIconValue(car, 'coolerTemp')}
                    dateDiff={this.helperDateDiff(car.coolerTemp.updatedAt)}
                  />
                )}
                <FocusGauge
                  title={translationsStore.translate(
                    'quickFocus.gauges.labelOdometer'
                  )}
                  valueWithIcon={this.helperGaugeIconValue(car, 'odometer')}
                  dateDiff={this.helperDateDiff(car.odometer.updatedAt)}
                />
                {car && car.connectedCarMaintenanceStatus !== 'active' && (
                  <FocusGauge
                    title={translationsStore.translate(
                      'quickFocus.gauges.labelGpsOdometer'
                    )}
                    valueWithIcon={this.helperGaugeIconValue(
                      car,
                      'gpsOdometer'
                    )}
                    dateDiff={this.helperDateDiff(car.gpsOdometer.updatedAt)}
                  />
                )}
                <FocusGauge
                  title={translationsStore.translate(
                    'quickFocus.gauges.labelBattery'
                  )}
                  valueWithIcon={this.helperGaugeIconValue(car, 'battery')}
                  dateDiff={this.helperDateDiff(car.battery.updatedAt)}
                />
                {car && car.connectedCarMaintenanceStatus !== 'active' && (
                  <FocusGauge
                    title={translationsStore.translate(
                      'quickFocus.gauges.labelOilLevel'
                    )}
                    valueWithIcon={this.helperGaugeIconValue(car, 'oilLevel')}
                    dateDiff={this.helperDateDiff(car.oilLevel.updatedAt)}
                  />
                )}
              </div>
            ) : (
              <div className={'BasicServiceLayout'}>
                <div className={'BasicServiceLayoutElement'}>
                  <div className={'BasicDashboardTitle'}>
                    <div>
                      {translationsStore.translate(
                        'quickFocus.gauges.dashboard'
                      )}
                    </div>
                  </div>
                  <div className='FocusGaugesBasic'>
                    <FocusGauge
                      title={translationsStore.translate(
                        'quickFocus.gauges.labelGpsOdometer'
                      )}
                      valueWithIcon={this.helperGaugeIconValue(
                        car,
                        'gpsOdometer'
                      )}
                      dateDiff={this.helperDateDiff(car.gpsOdometer.updatedAt)}
                    />
                    <FocusGauge
                      title={translationsStore.translate(
                        'quickFocus.gauges.labelBattery'
                      )}
                      valueWithIcon={this.helperGaugeIconValue(car, 'battery')}
                      dateDiff={this.helperDateDiff(car.battery.updatedAt)}
                    />
                  </div>
                </div>

                <div
                  className={'BasicServiceLayoutElement'}
                  style={{ marginLeft: '20px' }}
                >
                  <QuickGroupMaintenances car={car} />
                </div>
              </div>
            )}
            <LockUnlockPanel car={car} />
          </div>
        )}
        {car && (
          <RpmDataModal
            carId={car.id}
            car={this.props.car}
            visible={this.state.rpmModalVisible}
            onCancel={() =>
              this.setState({ rpmModalVisible: false }, () => {
                this.unsetModalInLocation();
              })
            }
          />
        )}
      </Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/App/ClientListWithMap/ClientDetail/QuickFocus/QuickFocus.js